import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Player, type PlayerRef } from '@remotion/player';
import { Video, computeTimeline, FPS, type Scene, type VideoFormat, type VideoSpec } from '@hyvay/video-engine';
import { absoluteAssets, videoService, type AttachedMedia, type VideoConfig, type VideoMessage, type VideoProjectDetail, type VideoRender } from '../../services/video.service';
import { getPublishedState } from '../../services/studio.service';
import { socialService, type ScheduledPost } from '../../services/social.service';
import { fmtCredits, fmtDuration, readTheme, writeTheme, type Theme } from '../../components/studio/studioUi';
import { StudioAccountMenu } from '../../components/studio/StudioAccountMenu';
import { SEOHead } from '../../components/seo/SEOHead';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { IMG } from '../landing/landingData';
import { BootOverlay, BOOT_MIN_MS_VIDEO, useBootOverlay } from '../landing/BootOverlay';
import { MediaPicker } from './MediaPicker';
import { RescheduleDialog } from './ScheduleView';
import { PostDrawer } from './PostDrawer';
import { PostDetailDrawer, groupPosts } from './PostDetailDrawer';
import { VideoInspector } from './VideoInspector';
import { BEAT, BRAND, Ico, Spin, VS_CSS, VS_FONT, VS_SERIF, aspectLabel, fmtMB, fmtStamp, fmtT, vsError, RENDER_STATUS } from './vsUi';
import '../landing/landing.css';

const SUGGESTIONS = ['Make the hook punchier', 'Switch to English captions', 'Add a 1:1 version for feed', 'Slower pace'];
/** Largest preview per aspect (the design's playerH). */
const PLAYER_MAX_H: Record<VideoFormat, number> = { portrait: 480, square: 400, landscape: 360 };

const floatCard: React.CSSProperties = {
    border: '1px solid var(--hv-line)', borderRadius: 14, background: 'var(--hv-page)',
    boxShadow: '0 18px 40px -28px var(--hv-shadow),0 2px 6px -3px rgba(0,0,0,0.06)',
};
const bubble: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 8px)', whiteSpace: 'nowrap', padding: '5px 9px', borderRadius: 7, background: '#24262A', color: '#F3F2EC',
    fontSize: 11.5, fontWeight: 600, opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s ease', zIndex: 12,
};
const Tt: React.FC<{ label: string; align?: 'left' | 'right' | 'center'; hidden?: boolean; children: React.ReactNode; style?: React.CSSProperties }> = ({ label, align = 'center', hidden, children, style }) => (
    <div className="hv-tt" style={{ position: 'relative', display: 'flex', flex: 'none', ...style }}>
        {children}
        {!hidden && <span className="hv-tt-bubble" style={{ ...bubble, ...(align === 'left' ? { left: 0 } : align === 'right' ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' }) }}>{label}</span>}
    </div>
);

/** Starts a download of a render (stored with Content-Disposition: attachment). */
function download(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
}

/** /video/:id — chat card | preview + timeline (or Media) | inspector. */
export const VideoStudio: React.FC = () => {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const { user, refreshUser } = useAuth();
    const [theme, setTheme] = useState<Theme>(readTheme);
    const [cfg, setCfg] = useState<VideoConfig | null>(null);
    const [video, setVideo] = useState<VideoProjectDetail | null>(null);
    const [messages, setMessages] = useState<VideoMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [thinking, setThinking] = useState(false);
    const [turnStage, setTurnStage] = useState('');
    const [turnStart, setTurnStart] = useState(0);
    const [selected, setSelected] = useState(0);
    const [renders, setRenders] = useState<VideoRender[]>([]);
    const [media, setMedia] = useState<AttachedMedia[]>([]);
    const [picker, setPicker] = useState(false);
    const [tab, setTab] = useState<'preview' | 'media'>('preview');
    const [chatOpen, setChatOpen] = useState(true);
    const [dlOpen, setDlOpen] = useState(false);
    const [postOpen, setPostOpen] = useState(false);
    const [posts, setPosts] = useState<ScheduledPost[]>([]);
    const [detailKey, setDetailKey] = useState<string | null>(null);
    const [moving, setMoving] = useState<ScheduledPost[] | null>(null);
    const [site, setSite] = useState<{ name: string; thumb: string | null; address: string | null } | null>(null);
    const [player, setPlayer] = useState<PlayerRef | null>(null);
    const playerRef = useRef<PlayerRef | null>(null);
    const bindPlayer = useCallback((p: PlayerRef | null) => { playerRef.current = p; setPlayer(p); }, []);
    const stageRef = useRef<HTMLDivElement>(null);
    const stage = useBoxSize(stageRef, !!video && tab === 'preview');
    const transcriptRef = useRef<HTMLDivElement>(null);
    const composerRef = useRef<HTMLTextAreaElement>(null);
    const saveTimer = useRef<number>();
    const firstSent = useRef(false);

    const changeTheme = (t: Theme) => { writeTheme(t); setTheme(t); };

    const load = useCallback(async () => {
        const v = await videoService.get(id);
        setVideo(v);
        setMessages(v.messages);
        setRenders(v.renders);
        setMedia(v.media);
        // A turn may still be running (e.g. after a refresh): show it and keep polling.
        setThinking(!!v.turn);
        setTurnStage(v.turn?.stage ?? '');
        if (v.turn) setTurnStart(new Date(v.turn.startedAt).getTime());
        return v;
    }, [id]);

    const send = useCallback(async (text: string) => {
        const message = text.trim();
        if (!message || thinking) return;
        setDraft('');
        setThinking(true);
        setTurnStage('');
        setTurnStart(Date.now());
        setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: message, createdAt: new Date().toISOString() }]);
        try {
            const r = await videoService.chat(id, message);
            setTurnStage(r.turn.stage);
        } catch (e) {
            setMessages((m) => [...m, { id: `e-${Date.now()}`, role: 'assistant', content: `Couldn't send that: ${vsError(e)}`, createdAt: new Date().toISOString() }]);
            setThinking(false);
        }
    }, [id, thinking]);

    // Poll the background turn; when it finishes, take spec + messages from the server.
    useEffect(() => {
        if (!thinking) return;
        const t = window.setInterval(() => {
            videoService.get(id).then((v) => {
                if (v.turn) {
                    setTurnStage(v.turn.stage);
                    return;
                }
                setVideo(v);
                setMessages(v.messages);
                setMedia(v.media);
                setThinking(false);
                setTurnStage('');
                setSelected(0);
                refreshUser().catch(() => {});
            }).catch(() => {});
        }, 2000);
        return () => window.clearInterval(t);
    }, [thinking, id, refreshUser]);

    const loadPosts = useCallback(() => {
        socialService.posts(id).then(setPosts).catch(() => {});
    }, [id]);

    useEffect(() => {
        videoService.config().then(setCfg).catch(() => {});
        loadPosts();
        load()
            .then((v) => {
                // Hand-off from the "new video" dialog: send its prompt once.
                const first = (location.state as { firstMessage?: string } | null)?.firstMessage;
                if (first && !firstSent.current && v.messages.length === 0) {
                    firstSent.current = true;
                    navigate(location.pathname, { replace: true, state: null });
                    send(first);
                }
            })
            .catch((e) => {
                // Don't leave the launch overlay up over a project that won't load.
                toast.error(vsError(e, 'Video not found.'));
                navigate('/dashboard', { replace: true });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // "From your site": the source app's thumbnail and live address.
    const appId = video?.sourceApplicationId ?? null;
    useEffect(() => {
        if (!appId) { setSite(null); return; }
        const app = cfg?.apps.find((a) => a.id === appId);
        setSite({ name: app?.name ?? 'Your site', thumb: app?.thumbnailUrl ?? null, address: null });
        let live = true;
        getPublishedState(appId).then((p) => {
            if (live && p?.url) setSite((s) => (s ? { ...s, address: p.url.replace(/^https?:\/\//, '').replace(/\/$/, '') } : s));
        });
        return () => { live = false; };
    }, [appId, cfg]);

    useEffect(() => {
        const el = transcriptRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages.length, thinking, turnStage]);

    // Poll while a render is in flight.
    const rendering = renders.some((r) => r.status === 'QUEUED' || r.status === 'RENDERING');
    useEffect(() => {
        if (!rendering) return;
        const t = window.setInterval(() => {
            videoService.get(id).then((v) => {
                setRenders(v.renders);
                const top = v.renders[0];
                if (top?.status === 'DONE') toast.success(`v${v.renders.length} is rendered and ready to download.`);
                if (top?.status === 'FAILED') { toast.error(`Render failed${top.error ? `: ${top.error}` : ''}. Your credits were refunded.`); refreshUser().catch(() => {}); }
            }).catch(() => {});
        }, 2500);
        return () => window.clearInterval(t);
    }, [rendering, id, toast, refreshUser]);

    // Poll while a post of this video is going out (or due within a minute).
    const posting = posts.some((p) => p.status === 'PUBLISHING' || (p.status === 'SCHEDULED' && new Date(p.scheduledAt).getTime() < Date.now() + 60_000));
    useEffect(() => {
        if (!posting) return;
        const t = window.setInterval(loadPosts, 5000);
        return () => window.clearInterval(t);
    }, [posting, loadPosts]);

    // Close the Download menu on any click outside it.
    useEffect(() => {
        if (!dlOpen) return;
        const close = (e: MouseEvent) => { if (!(e.target as Element).closest?.('[data-hv-pop]')) setDlOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [dlOpen]);

    /** Local edit + debounced save (inspector, timeline). */
    const patchSpec = useCallback((fn: (s: VideoSpec) => VideoSpec) => {
        setVideo((v) => {
            if (!v) return v;
            const spec = fn(v.spec);
            window.clearTimeout(saveTimer.current);
            saveTimer.current = window.setTimeout(() => {
                videoService.update(id, { spec }).then((r) => setVideo((cur) => (cur ? { ...cur, assets: r.assets } : cur))).catch((e) => toast.error(vsError(e)));
            }, 700);
            return { ...v, spec };
        });
    }, [id, toast]);

    /** `format` renders another aspect ratio (the 9:16 copy for TikTok / Reels). */
    const startRender = async (format?: VideoFormat) => {
        try {
            const r = await videoService.render(id, format);
            setRenders((rs) => [r, ...rs]);
            refreshUser().catch(() => {});
            toast.info(format ? 'Rendering a 9:16 copy.' : 'Render started. You can keep editing.');
        } catch (e) {
            toast.error(vsError(e));
        }
    };

    const rename = async (name: string) => {
        setVideo((v) => (v ? { ...v, name } : v));
        try {
            await videoService.update(id, { name });
        } catch (e) {
            toast.error(vsError(e));
        }
    };

    const attach = async (assets: { assetId: string }[]) => {
        try {
            setMedia(await videoService.attach(id, assets.map((a) => a.assetId)));
            setDraft((d) => d || 'Use the files I just attached.');
        } catch (e) {
            toast.error(vsError(e));
        }
    };
    const detach = async (assetId: string) => {
        try {
            setMedia(await videoService.detach(id, assetId));
        } catch (e) {
            toast.error(vsError(e));
        }
    };

    const assets = useMemo(() => (video ? absoluteAssets(video.assets) : {}), [video]);
    const timeline = useMemo(() => (video ? computeTimeline(video.spec, assets) : null), [video, assets]);
    // Preview plays the H.264 proxy of each clip when ready (HEVC is slow/unsupported in some browsers).
    const playerSpec = useMemo(() => {
        if (!video) return null;
        const proxy = new Map(media.filter((m) => m.proxyUrl).map((m) => [m.url, m.proxyUrl!]));
        if (!proxy.size) return video.spec;
        return { ...video.spec, scenes: video.spec.scenes.map((s) => (s.type === 'video' && proxy.has(s.videoUrl) ? { ...s, videoUrl: proxy.get(s.videoUrl)! } : s)) };
    }, [video, media]);
    const groups = useMemo(() => groupPosts(posts), [posts]);
    const detail = groups.find((g) => g.key === detailKey) ?? null;

    // The design's launch overlay ("Creating Your Video" / "Opening <name>"), carried over
    // from the dashboard click; a direct visit or refresh shows "Opening Studio" while loading.
    const boot = useBootOverlay(!!video && !!timeline, { kind: 'video', fallback: 'OPENING STUDIO', minMs: BOOT_MIN_MS_VIDEO });

    if (!video || !timeline) {
        return (
            <div className="hv-root hv-vs" data-hv-theme={theme} style={{ height: '100vh', background: 'var(--hv-desk)', fontFamily: VS_FONT }}>
                <BootOverlay kind="video" copy={boot?.copy ?? 'OPENING STUDIO'} dark={theme === 'dark'} />
            </div>
        );
    }

    const spec = video.spec;
    const hasScenes = spec.scenes.length > 0;
    // First turn of a fresh project: the design's "CREATING" card over the preview.
    const creating = thinking && !messages.some((m) => m.role === 'assistant');
    const ready = hasScenes && !creating;
    const versionOf = (r: VideoRender) => `v${renders.length - renders.findIndex((x) => x.id === r.id)}`;
    const latestDone = renders.find((r) => r.status === 'DONE' && r.url);
    const inFlight = renders.find((r) => r.status === 'QUEUED' || r.status === 'RENDERING');
    const renderCost = cfg?.renderCost ?? 10;
    const renderOk = ready && !!cfg?.renderEnabled && !rendering;
    const renderTip = !ready ? 'Your video is still being created'
        : !cfg?.renderEnabled ? 'Rendering isn\'t available on this server yet'
            : rendering ? 'Rendering the 1080p MP4'
                : latestDone ? `Render a new MP4 · ${renderCost} credits` : `Render the final 1080p MP4 · ${renderCost} credits`;
    const renderPct = inFlight ? Math.round((inFlight.progress || 0) * 100) : 0;
    const ratio = timeline.width / timeline.height;
    const availH = Math.max(0, stage.h - 44 - 54);
    const availW = Math.max(0, stage.w - 44);
    const ph = Math.floor(Math.min(PLAYER_MAX_H[spec.format], availH, availW / ratio));
    const pw = Math.floor(ph * ratio);
    const lastAssistant = [...messages].reverse().findIndex((m) => m.role === 'assistant');
    const lastAssistantIdx = lastAssistant < 0 ? -1 : messages.length - 1 - lastAssistant;
    const firstUserIdx = messages.findIndex((m) => m.role === 'user');
    const canSend = !!draft.trim() && !thinking;
    const seek = (i: number) => {
        setSelected(i);
        const t = timeline.scenes[i];
        if (t && !t.skipped) playerRef.current?.seekTo(t.from + Math.min(6, Math.max(0, t.frames - 1)));
    };

    const seg = (on: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: 7, height: 30, padding: '0 14px', border: 0, borderRadius: 999, background: on ? 'var(--hv-card)' : 'transparent',
        color: on ? 'var(--hv-mint-ink)' : 'var(--hv-body)', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: on ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
    });

    return (
        <div className="hv-root hv-vs" data-hv-theme={theme} style={{ height: '100vh', padding: 0, background: 'var(--hv-desk)', fontFamily: VS_FONT, color: 'var(--hv-ink)', WebkitFontSmoothing: 'antialiased' }}>
            <style>{VS_CSS}</style>
            <SEOHead title={`Video - ${video.name}`} noindex={true} />
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--hv-page)' }}>
                {/* Header */}
                <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, height: 62, padding: '0 16px', borderBottom: '1px solid var(--hv-line)', background: 'var(--hv-card)' }}>
                    <Link to="/dashboard" title="Back to dashboard" className="hv-vs-mint" style={{ display: 'flex', flex: 'none', alignItems: 'center', gap: 6, height: 36, padding: '0 10px 0 6px', borderRadius: 10, color: 'var(--hv-body)' }}>
                        {Ico.chevL(15)}
                        {theme === 'dark'
                            ? <img src={IMG.logoDark} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover' }} />
                            : <img src={IMG.logoLight} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover', mixBlendMode: 'multiply' }} />}
                    </Link>
                    <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 5, height: 22, marginLeft: -10, padding: '0 9px', borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', fontSize: 9, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>
                        {Ico.film()} VIDEO
                    </span>
                    <span style={{ flex: 'none', width: 1, height: 22, background: 'var(--hv-line-2)' }} />
                    <NameEditor name={video.name} onRename={rename} />
                    <div style={{ flex: 1 }} />
                    <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 3, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                        <button type="button" onClick={() => setTab('preview')} style={seg(tab === 'preview')}>{Ico.playCircle()}<span className="hv-vs-tab-label">Preview</span></button>
                        <button type="button" onClick={() => setTab('media')} style={seg(tab === 'media')}>{Ico.media()}<span className="hv-vs-tab-label">Media</span></button>
                    </div>
                    <div style={{ flex: 1 }} />
                    <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {/* Download split button */}
                        <div className="hv-tt" data-hv-pop="1" style={{ position: 'relative', display: 'flex' }}>
                            <div style={{ display: 'flex', height: 34, border: '1px solid var(--hv-line)', borderRadius: 999, overflow: 'hidden', opacity: latestDone ? 1 : 0.5 }}>
                                <button type="button" className="hv-vs-dl hv-vs-mint" disabled={!latestDone} onClick={() => latestDone?.url && download(latestDone.url)} style={{ display: 'flex', alignItems: 'center', gap: 7, height: '100%', padding: '0 12px 0 13px', border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', cursor: latestDone ? 'pointer' : 'not-allowed' }}>
                                    {Ico.download()}{latestDone ? `Download · ${versionOf(latestDone)}` : 'Download'}
                                </button>
                                <button type="button" className="hv-vs-mint" aria-label="Choose version" aria-haspopup="menu" aria-expanded={dlOpen} disabled={!renders.length} onClick={() => setDlOpen((o) => !o)} style={{ width: 30, height: '100%', border: 0, borderLeft: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: renders.length ? 'pointer' : 'not-allowed' }}>
                                    {Ico.chevD(12)}
                                </button>
                            </div>
                            {!dlOpen && <span className="hv-tt-bubble" style={{ ...bubble, left: '50%', transform: 'translateX(-50%)' }}>{latestDone ? `Download ${versionOf(latestDone)} (MP4)` : 'Render the video first'}</span>}
                            {dlOpen && (
                                <div role="menu" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 40, padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 24px 44px -20px var(--hv-shadow)', animation: 'hvRise 0.18s ease both', width: 300 }}>
                                    <div style={{ padding: '6px 8px 8px', fontSize: 12, fontWeight: 600, color: 'var(--hv-muted)' }}>Rendered versions</div>
                                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                        {renders.map((r) => (
                                            <div key={r.id} className="hv-vs-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderRadius: 5 }}>
                                                <span style={{ flex: 'none', fontSize: 11.5, fontWeight: 700 }}>{versionOf(r)}</span>
                                                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.error ?? undefined}>
                                                    {[aspectLabel(r.format), fmtStamp(new Date(r.createdAt)), fmtMB(r.bytes)].filter(Boolean).join(' · ')}
                                                </span>
                                                {r.status === 'DONE' && r.url ? (
                                                    <button type="button" className="hv-vs-under" onClick={() => { setDlOpen(false); download(r.url!); }} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 5, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-brand-ink)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                                                        {Ico.download()}Download
                                                    </button>
                                                ) : (
                                                    <span style={{ flex: 'none', fontSize: 12, color: r.status === 'FAILED' ? 'var(--hv-danger)' : 'var(--hv-muted)' }}>
                                                        {RENDER_STATUS[r.status]}{r.status === 'RENDERING' ? ` ${Math.round(r.progress * 100)}%` : ''}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Post */}
                        <Tt label={latestDone ? 'Post to your channels' : 'Render the video first'}>
                            <button type="button" className={latestDone ? 'hv-vs-mint' : undefined} data-track="video_schedule_open" aria-haspopup="dialog" disabled={!latestDone} onClick={() => setPostOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 13px', border: '1px solid var(--hv-line)', borderRadius: 999, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', cursor: latestDone ? 'pointer' : 'not-allowed', opacity: latestDone ? 1 : 0.5 }}>
                                {Ico.share()}Post
                            </button>
                        </Tt>
                        {/* Render */}
                        <Tt label={renderTip}>
                            <button type="button" data-track="video_render" className={renderOk ? 'hv-vs-brand' : undefined} disabled={!renderOk} onClick={() => startRender()} style={{ position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 7, height: 34, minWidth: 104, justifyContent: 'center', padding: '0 16px', border: 0, borderRadius: 999, background: ready && cfg?.renderEnabled ? BRAND : 'color-mix(in srgb,var(--brand,#13422C) 45%,var(--hv-page-2))', color: '#FFFDF7', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', cursor: renderOk ? 'pointer' : rendering ? 'progress' : 'not-allowed' }}>
                                {rendering && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${renderPct}%`, background: 'rgba(255,253,247,0.16)', transition: 'width 0.4s ease' }} />}
                                {rendering ? <Spin light /> : <span style={{ position: 'relative', display: 'flex' }}>{Ico.film(13, 2.2)}</span>}
                                <span style={{ position: 'relative' }}>{rendering ? `Rendering${renderPct ? ` ${renderPct}%` : ''}` : latestDone ? 'Render again' : 'Render'}</span>
                            </button>
                        </Tt>
                        <StudioAccountMenu theme={theme} onTheme={changeTheme} />
                    </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: 'flex', background: 'var(--hv-page-2)' }}>
                    {/* Chat card */}
                    {chatOpen && (
                        <div className="hv-vs-chat" style={{ ...floatCard, position: 'relative', zIndex: 4, flex: 'none', width: 340, display: 'flex', flexDirection: 'column', minHeight: 0, margin: '8px 0 8px 8px' }}>
                            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, height: 52, padding: '0 16px', borderBottom: '1px solid var(--hv-line)' }}>
                                <span style={{ fontSize: 13.5, fontWeight: 600 }}>Chat</span>
                                <div style={{ flex: 1 }} />
                                <Tt label="Collapse" align="right">
                                    <button type="button" className="hv-vs-muted" aria-label="Collapse chat" onClick={() => setChatOpen(false)} style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer' }}>{Ico.collapse()}</button>
                                </Tt>
                            </div>
                            <div ref={transcriptRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 22 }}>
                                {messages.length === 0 && !thinking && (
                                    <>
                                        {site && <SiteChip site={site} />}
                                        <div style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--hv-body)' }}>
                                            Tell me about the video you want: your business, the offer, how long, and whether it needs a voiceover. You can also edit the scenes on the right directly.
                                        </div>
                                    </>
                                )}
                                {messages.map((m, i) => (m.role === 'user' ? (
                                    <div key={m.id} style={{ alignSelf: 'flex-end', maxWidth: '88%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                        {i === firstUserIdx && site && <SiteChip site={site} />}
                                        <div style={{ padding: '10px 13px', borderRadius: 10, background: 'var(--hv-page-2)', color: 'var(--hv-ink)', fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</div>
                                    </div>
                                ) : (
                                    <AssistantMessage key={m.id} message={m} suggestions={i === lastAssistantIdx && !thinking ? SUGGESTIONS : null} onSuggest={(s) => { setDraft(s); composerRef.current?.focus(); }} />
                                )))}
                                {thinking && (
                                    <div style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, fontSize: 12.5 }}>
                                        <Spin />
                                        <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--hv-ink)' }}>{turnStage || 'Reading your prompt'}</span>
                                        {media.some((x) => x.kind === 'video') && <span style={{ flex: 'none', color: 'var(--hv-muted)' }}>· you can leave this page</span>}
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 'none', padding: '14px 16px 16px' }}>
                                <form className="hv-composer" onSubmit={(e) => { e.preventDefault(); send(draft); }} style={{ padding: 12, borderRadius: 12, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', transition: 'border-color 0.18s ease,box-shadow 0.18s ease' }}>
                                    {media.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                                            {media.map((f) => (
                                                <span key={f.assetId} title={f.originalName} style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: '100%', height: 30, padding: '0 4px 0 10px', borderRadius: 999, background: 'var(--hv-card-2)', border: '1px solid var(--hv-line)', fontSize: 12, fontWeight: 600, color: 'var(--hv-ink)' }}>
                                                    <span style={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.originalName}</span>
                                                    <button type="button" className="hv-vs-ghost" aria-label={`Remove ${f.originalName}`} onClick={() => detach(f.assetId)} style={{ width: 22, height: 22, border: 0, borderRadius: 999, background: 'transparent', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>{Ico.x()}</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <textarea
                                        ref={composerRef}
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); } }}
                                        rows={2}
                                        placeholder="Describe what you want to change..."
                                        style={{ width: '100%', border: 0, outline: 'none', resize: 'none', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, lineHeight: 1.6, fontFamily: 'inherit', padding: 0 }}
                                    />
                                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <button type="button" className="hv-vs-ghost" aria-label="Attach files" title="Attach photos, clips or a logo" onClick={() => setPicker(true)} style={{ flex: 'none', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-body)', cursor: 'pointer' }}>{Ico.plus()}</button>
                                        <button type="button" className="hv-vs-mint" title="Top up credits" onClick={() => navigate('/account/billing')} style={{ display: 'flex', alignItems: 'center', gap: 5, height: 28, padding: '0 10px', whiteSpace: 'nowrap', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', fontSize: 11.5, fontWeight: 600, color: 'var(--hv-mint-ink)', cursor: 'pointer' }}>
                                            {Ico.coins()} {fmtCredits(user?.credits)} credits
                                        </button>
                                        <div style={{ flex: 1 }} />
                                        <button type="submit" aria-label="Send message" disabled={!canSend} style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, borderRadius: 999, background: canSend ? BRAND : 'var(--hv-page-2)', color: canSend ? '#FFFDF7' : 'var(--hv-muted)', cursor: canSend ? 'pointer' : 'not-allowed' }}>{Ico.up()}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Center */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', background: 'var(--hv-page-2)' }}>
                        {!chatOpen && (
                            <Tt label="Expand chat" align="left" style={{ position: 'absolute', top: 12, left: 12, zIndex: 5 }}>
                                <button type="button" className="hv-vs-ghost" aria-label="Expand chat" onClick={() => setChatOpen(true)} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-body)', cursor: 'pointer' }}>{Ico.expand()}</button>
                            </Tt>
                        )}
                        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.7, backgroundImage: 'radial-gradient(circle,var(--hv-line) 0.8px,transparent 1.2px)', backgroundSize: '14px 14px' }} />

                        {tab === 'preview' ? (
                            <div style={{ position: 'relative', zIndex: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <div ref={stageRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                                    <div style={{ position: 'relative', flex: 'none', width: pw, height: ph, overflow: 'hidden', background: '#0E1110', boxShadow: '0 40px 64px -36px var(--hv-shadow)' }}>
                                        {hasScenes && pw > 0 && (
                                            <Player
                                                ref={bindPlayer}
                                                component={Video}
                                                inputProps={{ spec: playerSpec ?? spec, assets }}
                                                durationInFrames={timeline.durationInFrames}
                                                compositionWidth={timeline.width}
                                                compositionHeight={timeline.height}
                                                fps={FPS}
                                                clickToPlay
                                                initialFrame={Math.min(45, timeline.durationInFrames - 1)}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        )}
                                        {(creating || !hasScenes) && <CreatingCard stage={turnStage} start={turnStart} running={thinking} />}
                                    </div>
                                    <PlayBar player={player} durationInFrames={timeline.durationInFrames} width={pw} disabled={!ready} />
                                </div>
                                {hasScenes && (
                                    <Timeline spec={spec} timeline={timeline} selected={selected} onSelect={seek} player={player} dim={creating} />
                                )}
                            </div>
                        ) : (
                            <MediaTab
                                media={media}
                                logoUrl={spec.brand.logoUrl}
                                onUpload={() => setPicker(true)}
                                onRemove={detach}
                            />
                        )}
                    </div>

                    {ready && (
                        <VideoInspector
                            spec={spec}
                            cfg={cfg}
                            selected={selected}
                            patchSpec={patchSpec}
                            groups={groups}
                            canPost={!!latestDone}
                            onSchedule={() => setPostOpen(true)}
                            onOpenPost={(g) => setDetailKey(g.key)}
                            onScheduler={() => navigate('/dashboard/scheduler')}
                        />
                    )}
                </div>
            </div>

            {boot && <BootOverlay kind="video" copy={boot.copy} hiding={boot.hiding} dark={theme === 'dark'} />}
            <MediaPicker open={picker} onClose={() => setPicker(false)} exclude={media.map((m) => m.assetId)} onDone={attach} />
            {postOpen && (
                <PostDrawer
                    onClose={() => setPostOpen(false)}
                    videoName={video.name}
                    renders={renders}
                    renderCost={renderCost}
                    onRenderPortrait={() => startRender('portrait')}
                    onScheduled={loadPosts}
                    theme={theme}
                />
            )}
            {detail && (
                <PostDetailDrawer
                    group={detail}
                    videoName={video.name}
                    renders={renders}
                    theme={theme}
                    onClose={() => setDetailKey(null)}
                    onEdit={(ps) => setMoving(ps)}
                    onChanged={loadPosts}
                />
            )}
            <RescheduleDialog post={moving} maxAheadDays={90} theme={theme} onClose={() => setMoving(null)} onSaved={() => { loadPosts(); setDetailKey(null); }} />
        </div>
    );
};

/* ------------------------------------------------------------------ parts */

/** Inline rename: Enter / blur commits, Escape cancels. */
const NameEditor: React.FC<{ name: string; onRename: (n: string) => void }> = ({ name, onRename }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => { if (editing) ref.current?.select(); }, [editing]);
    const commit = () => {
        setEditing(false);
        const next = draft.trim();
        if (next && next !== name) onRename(next);
    };
    return (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {editing ? (
                <input
                    ref={ref}
                    value={draft}
                    placeholder="Project name"
                    aria-label="Project name"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(name); setEditing(false); } }}
                    onBlur={commit}
                    style={{ marginLeft: -11, flex: 'none', width: 230, height: 34, padding: '0 11px', borderRadius: 6, border: '1px solid var(--brand,#13422C)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 14, fontWeight: 700, outline: 'none' }}
                />
            ) : (
                <Tt label="Rename project" align="left" style={{ marginLeft: -11 }}>
                    <button type="button" className="hv-vs-mint" onClick={() => { setDraft(name); setEditing(true); }} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 11px', border: 0, borderRadius: 10, background: 'transparent', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)', cursor: 'pointer', whiteSpace: 'nowrap', maxWidth: 230, overflow: 'hidden' }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                        <span style={{ flex: 'none', display: 'flex' }}>{Ico.pencil()}</span>
                    </button>
                </Tt>
            )}
        </div>
    );
};

const SiteChip: React.FC<{ site: { name: string; thumb: string | null; address: string | null } }> = ({ site }) => (
    <div style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px 6px 6px', borderRadius: 12, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', maxWidth: '100%' }}>
        {site.thumb
            ? <img src={site.thumb} alt="" style={{ flex: 'none', width: 42, height: 30, borderRadius: 6, objectFit: 'cover', objectPosition: 'top', display: 'block', border: '1px solid var(--hv-line)' }} />
            : <span style={{ flex: 'none', width: 42, height: 30, borderRadius: 6, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }} />}
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 9, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>FROM YOUR SITE</span>
            <span style={{ fontSize: 10.5, color: 'var(--hv-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{site.address ?? site.name}</span>
        </span>
    </div>
);

/** Step summary ("Worked through 6 steps · 16.0s", expandable), the reply, and follow-up chips. */
const AssistantMessage: React.FC<{ message: VideoMessage; suggestions: string[] | null; onSuggest: (s: string) => void }> = ({ message, suggestions, onSuggest }) => {
    const [open, setOpen] = useState(false);
    const steps = message.steps ?? [];
    const total = steps.reduce((t, s) => t + s.ms, 0);
    return (
        <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {steps.length > 0 && (
                <>
                    <button type="button" className="hv-vs-steps" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ alignSelf: 'flex-start', maxWidth: '100%', minWidth: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, height: 28, padding: '0 8px 0 0', border: 0, background: 'transparent', color: 'var(--hv-muted)', fontSize: 12.5, cursor: 'pointer' }}>
                        <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Worked through {steps.length} step{steps.length === 1 ? '' : 's'} · {fmtDuration(total)}</span>
                        {Ico.chevR(13, { transform: `rotate(${open ? 90 : 0}deg)`, transition: 'transform 0.2s ease' })}
                    </button>
                    {open && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingLeft: 2, marginTop: -4, animation: 'hvFade 0.2s ease both' }}>
                            {steps.map((s, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--hv-muted)' }}>
                                    <span style={{ flex: 'none', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Ico.check(12, 'var(--hv-leaf)', 3.4)}</span>
                                    <span style={{ flex: 1, minWidth: 0 }}>{s.label}</span>
                                    <span style={{ flex: 'none', fontSize: 9.5 }}>{fmtDuration(s.ms)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
            <div style={{ marginTop: steps.length ? -8 : 0, fontSize: 13.5, lineHeight: 1.65, color: 'var(--hv-ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.content}</div>
            {suggestions && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {suggestions.map((s) => (
                        <button key={s} type="button" className="hv-vs-mint" onClick={() => onSuggest(s)} style={{ display: 'flex', alignItems: 'center', height: 30, padding: '0 12px', border: '1px solid var(--hv-line-2)', borderRadius: 999, background: 'transparent', color: 'var(--hv-body)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>{s}</button>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Dark "CREATING · NN%" card over the preview while the first turn runs. */
const CreatingCard: React.FC<{ stage: string; start: number; running: boolean }> = ({ stage, start, running }) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!running) return;
        const t = window.setInterval(() => setNow(Date.now()), 500);
        return () => window.clearInterval(t);
    }, [running]);
    // No real progress from the server: ease towards 95% (about 2/3 after 45 s).
    const pct = running ? Math.round(95 * (1 - Math.exp(-Math.max(0, now - (start || now)) / 40_000))) : 0;
    return (
        <div style={{ position: 'absolute', inset: 0, zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '8%', background: '#0E1110', color: '#F3F2EC', textAlign: 'center' }}>
            {running ? (
                <>
                    <span style={{ width: 28, height: 28, borderRadius: 999, border: '2.5px solid rgba(255,255,255,0.2)', borderTopColor: '#FFFDF7', animation: 'hvSpin 0.8s linear infinite' }} />
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{stage || 'Reading your prompt'}</span>
                    <span style={{ width: '62%', height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: '#FFFDF7', transition: 'width 0.6s ease' }} />
                    </span>
                    <span style={{ fontSize: 10, letterSpacing: '0.06em', opacity: 0.7 }}>CREATING · {pct}%</span>
                </>
            ) : (
                <span style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.8 }}>No scenes yet. Describe your video in the chat.</span>
            )}
        </div>
    );
};

/** Round play / pause + "0:07 / 0:30", driven by the Player's events. */
const PlayBar: React.FC<{ player: PlayerRef | null; durationInFrames: number; width: number; disabled: boolean }> = ({ player, durationInFrames, width, disabled }) => {
    const [playing, setPlaying] = useState(false);
    const [frame, setFrame] = useState(0);
    useEffect(() => {
        if (!player) return;
        setFrame(player.getCurrentFrame());
        setPlaying(player.isPlaying());
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        const onFrame = (e: { detail: { frame: number } }) => setFrame(e.detail.frame);
        player.addEventListener('play', onPlay);
        player.addEventListener('pause', onPause);
        player.addEventListener('ended', onPause);
        player.addEventListener('frameupdate', onFrame);
        player.addEventListener('seeked', onFrame);
        return () => {
            player.removeEventListener('play', onPlay);
            player.removeEventListener('pause', onPause);
            player.removeEventListener('ended', onPause);
            player.removeEventListener('frameupdate', onFrame);
            player.removeEventListener('seeked', onFrame);
        };
    }, [player]);
    const toggle = () => {
        if (!player) return;
        if (player.isPlaying()) { player.pause(); return; }
        if (player.getCurrentFrame() >= durationInFrames - 1) player.seekTo(0);
        player.play();
    };
    return (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, width, maxWidth: '100%' }}>
            <button type="button" className={disabled ? undefined : 'hv-vs-brand'} aria-label={playing ? 'Pause' : 'Play'} disabled={disabled || !player} onClick={toggle} style={{ flex: 'none', width: 40, height: 40, borderRadius: 999, border: 0, background: BRAND, color: '#FFFDF7', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'default' : 'pointer' }}>
                {playing ? Ico.pause() : Ico.play()}
            </button>
            <span style={{ flex: 'none', fontSize: 11, color: 'var(--hv-body)', fontVariantNumeric: 'tabular-nums' }}>{fmtT(frame / FPS)} / {fmtT(durationInFrames / FPS)}</span>
        </div>
    );
};

type Timed = ReturnType<typeof computeTimeline>;

/** Scene cards under the preview: click = select + seek; the playing scene shows a progress line. */
const Timeline: React.FC<{ spec: VideoSpec; timeline: Timed; selected: number; onSelect: (i: number) => void; player: PlayerRef | null; dim: boolean }> = ({ spec, timeline, selected, onSelect, player, dim }) => {
    const [frame, setFrame] = useState(-1);
    useEffect(() => {
        if (!player) return;
        const onFrame = (e: { detail: { frame: number } }) => setFrame(e.detail.frame);
        player.addEventListener('frameupdate', onFrame);
        player.addEventListener('seeked', onFrame);
        return () => { player.removeEventListener('frameupdate', onFrame); player.removeEventListener('seeked', onFrame); };
    }, [player]);
    return (
        <div style={{ flex: 'none', margin: '0 12px 12px', ...floatCard, opacity: dim ? 0.45 : 1, pointerEvents: dim ? 'none' : 'auto', transition: 'opacity 0.3s ease', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: 12, scrollbarWidth: 'thin' }}>
                {spec.scenes.map((s, i) => {
                    const t = timeline.scenes[i];
                    const on = i === selected;
                    const skipped = !!t?.skipped;
                    const f = t && !skipped && frame >= t.from && frame < t.from + t.frames ? (frame - t.from) / t.frames : 0;
                    return (
                        <button key={s.id} type="button" aria-pressed={on} className={on ? undefined : 'hv-vs-bd2'} onClick={() => onSelect(i)} title={skipped ? 'Hidden: the end card is switched off' : undefined} style={{ position: 'relative', flex: 'none', width: 168, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 3, padding: '11px 13px 12px', borderRadius: 12, border: on ? `2px solid ${BRAND}` : '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-ink)', textAlign: 'left', cursor: 'pointer', overflow: 'hidden', opacity: skipped ? 0.45 : 1 }}>
                            <span style={{ fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>Scene {i + 1} · {skipped ? 'Hidden' : `${((t?.frames ?? 0) / FPS).toFixed(1)}s`}</span>
                            <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13.5, fontWeight: 700 }}>{BEAT[s.type]}</span>
                            <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, color: 'var(--hv-body)' }}>{sceneSummary(s) || '—'}</span>
                            <span style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: `${f > 0 && f < 1 ? f * 100 : 0}%`, background: BRAND, transition: 'width 0.1s linear' }} />
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

function sceneSummary(s: Scene): string {
    switch (s.type) {
        case 'title': case 'features': case 'cta': return s.title;
        case 'statement': return s.text;
        case 'stat': return `${s.value} ${s.label}`;
        case 'quote': return s.quote;
        case 'image': return s.title || s.caption || '';
        case 'screenshot': return s.caption || s.url || '';
        case 'video': return s.title || s.caption || 'Video clip';
    }
}

/** Media tab: upload tile, the user's attached files and their logo. Music and voiceover are server-side, not user media. */
const MediaTab: React.FC<{ media: AttachedMedia[]; logoUrl?: string; onUpload: () => void; onRemove: (id: string) => void }> = ({ media, logoUrl, onUpload, onRemove }) => {
    const tile: React.CSSProperties = { position: 'relative', aspectRatio: '1', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hv-line)', background: 'var(--hv-card)', display: 'flex', flexDirection: 'column' };
    const name: React.CSSProperties = { padding: '8px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
    return (
        <div style={{ position: 'relative', zIndex: 2, flex: 1, minHeight: 0, overflow: 'auto', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontFamily: VS_SERIF, fontWeight: 400, fontSize: 28, letterSpacing: '-0.003em', wordSpacing: '0.06em' }}>Media</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 14 }}>
                <button type="button" className="hv-vs-bd" onClick={onUpload} style={{ aspectRatio: '1', borderRadius: 14, border: '1.5px dashed var(--hv-line-2)', background: 'var(--hv-card)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16, textAlign: 'center', color: 'var(--hv-muted)', cursor: 'pointer' }}>
                    {Ico.upload()}
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--hv-ink)' }}>Upload photos or clips</span>
                    <span style={{ fontSize: 12, lineHeight: 1.5 }}>Used in place of generated scenes</span>
                </button>
                {media.map((m) => (
                    <div key={m.assetId} style={tile} title={m.originalName}>
                        <div style={{ flex: 1, minHeight: 0, background: 'var(--hv-page-2)', position: 'relative' }}>
                            {m.kind === 'video'
                                ? <video src={m.proxyUrl || m.url} muted preload="metadata" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <img src={m.url} alt={m.alt || m.originalName} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                            {m.kind === 'video' && <span style={{ position: 'absolute', left: 8, bottom: 8, height: 20, padding: '0 7px', borderRadius: 999, display: 'flex', alignItems: 'center', background: 'rgba(20,24,22,0.72)', color: '#fff', fontSize: 11, fontWeight: 600 }}>{m.durationSec ? fmtT(m.durationSec) : 'Clip'}</span>}
                            <button type="button" aria-label={`Remove ${m.originalName}`} title="Remove" onClick={() => onRemove(m.assetId)} style={{ position: 'absolute', right: 8, top: 8, width: 24, height: 24, borderRadius: 999, border: 0, background: 'rgba(20,24,22,0.72)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>{Ico.x()}</button>
                        </div>
                        <div style={name}>{m.originalName}</div>
                    </div>
                ))}
                {logoUrl && (
                    <div style={tile}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--hv-page-2)' }}>
                            <img src={logoUrl} alt="Logo" style={{ width: '56%', height: '56%', objectFit: 'contain' }} />
                        </div>
                        <div style={name}>{decodeURIComponent(logoUrl.split('?')[0].split('/').pop() || 'Logo')}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

/** Content-box size of an element, tracked live (attaches once `ready`). */
function useBoxSize(ref: React.RefObject<HTMLElement>, ready: boolean) {
    const [size, setSize] = useState({ w: 0, h: 0 });
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width + 44, h: e.contentRect.height + 44 }));
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref, ready]);
    return size;
}
